import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, setTokens, clearTokens } from '@/react-app/config/api';

interface User {
  id: number;
  email: string;
  nome: string;
  role: string; // ADMIN, GESTOR, USUARIO, COMPLIANCE
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'airtrust_token';
const REFRESH_TOKEN_KEY = 'airtrust_refresh_token';
const USER_KEY = 'airtrust_user';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar dados do localStorage ao montar
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        console.log('[Auth] Verificando localStorage...');
        console.log('[Auth] Token presente:', !!storedToken);
        console.log('[Auth] User presente:', !!storedUser);

        if (storedToken && storedUser) {
          // Carregar token e user - validação será feita no primeiro request via useApi
          setToken(storedToken);
          setUser(JSON.parse(storedUser));

          // Sincronizar com api.ts
          const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          setTokens(storedToken, storedRefreshToken || undefined);

          console.log('[Auth] Token carregado do localStorage');
        } else {
          // 🔓 DEV AUTO-LOGIN: Fazer login automático em desenvolvimento
          if (import.meta.env.MODE === 'development' || import.meta.env.DEV) {
            console.log('[Auth] 🔓 DEV MODE - Tentando auto-login...');
            try {
              const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email: 'admin@airtrust.com',
                  password: 'Admin@123',
                }),
              });

              if (response.ok) {
                const data = await response.json();
                if (data.success && data.data?.accessToken) {
                  const newToken = data.data.accessToken;
                  const newRefreshToken = data.data.refreshToken;
                  const newUser = data.data.user;

                  localStorage.setItem(TOKEN_KEY, newToken);
                  localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
                  localStorage.setItem(USER_KEY, JSON.stringify(newUser));

                  setToken(newToken);
                  setUser(newUser);
                  setTokens(newToken, newRefreshToken);

                  console.log('[Auth] ✅ Auto-login DEV concluído (RBAC bypass ativo no backend)');
                }
              }
            } catch (devLoginError) {
              console.warn('[Auth] Auto-login DEV falhou:', devLoginError);
            }
          }
          console.log('[Auth] Nenhum token salvo, usuário não autenticado');
        }
      } catch (error) {
        console.error('[Auth] Erro ao carregar dados:', error);
        // Limpar dados corrompidos
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  const login = useCallback(async (credentials: { email: string; password: string }) => {
    setIsLoading(true);
    try {
      console.log('[Auth] Iniciando login...');
      console.log('[Auth] API URL:', API_BASE_URL);
      console.log('[Auth] Credenciais:', { email: credentials.email, password: '***' });

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          senha: credentials.password, // Backend espera 'senha'
        }),
      });

      console.log('[Auth] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro ao fazer login' }));
        console.error('[Auth] Erro na resposta:', errorData);
        throw new Error(errorData.error || 'Credenciais inválidas');
      }

      const data = await response.json();
      console.log('[Auth] Response data:', { success: data.success, hasData: !!data.data });

      if (!data.success || !data.data) {
        throw new Error(data.error || 'Resposta inválida do servidor');
      }

      const { accessToken, refreshToken: newRefreshToken, user: userData } = data.data;

      // Salvar no estado
      setToken(accessToken);
      setUser(userData);

      // Sincronizar com api.ts
      setTokens(accessToken, newRefreshToken);

      // Persistir no localStorage
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      if (newRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      }

      console.log('[Auth] Login bem-sucedido:', userData.email);
    } catch (error) {
      console.error('[Auth] Erro no login:', error);
      // Limpar estado em caso de erro
      setToken(null);
      setUser(null);
      clearTokens();
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);

    // Limpar api.ts
    clearTokens();

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    console.log('[Auth] Logout realizado');
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!storedRefreshToken) {
        throw new Error('Refresh token não encontrado');
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (!response.ok) {
        throw new Error('Falha ao renovar token');
      }

      const data = await response.json();

      if (!data.success || !data.data) {
        throw new Error('Resposta inválida do servidor');
      }

      const { accessToken, refreshToken: newRefreshToken } = data.data;

      // Atualizar token
      setToken(accessToken);

      // Sincronizar com api.ts
      setTokens(accessToken, newRefreshToken);

      localStorage.setItem(TOKEN_KEY, accessToken);
      if (newRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      }

      console.log('[Auth] Token renovado com sucesso');
    } catch (error) {
      console.error('[Auth] Erro ao renovar token:', error);
      // Em caso de falha, fazer logout
      logout();
      throw error;
    }
  }, [logout]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
